import React, { useMemo, useState } from 'react';

import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, CircularProgress, Link, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';

import { FRAUD_STATUS_LABELS, ORDER_EDIT_LOG_FIELD_LABELS, ORDER_STATUS_LABELS } from '~/consts/order';
import stores from '~/consts/stores';

import { useGetOrderEditLogs } from '~/hooks/apis/orders/useGetOrderEditLogs';
import { useGetUsersSummaryByIds } from '~/hooks/apis/users/useGetUsersSummaryByIds';

import type { OrderEditLogEntry, OrderEditLogField } from '~/services/OrdersService';
import { formatDateLongMonthWithMeridiemShort } from '~/services/TimeService';

const PAGE_SIZE = 20;

function formatFieldValue(field: OrderEditLogField, value: string | null): string {
  if (value === null || value === '') {
    return '—';
  }

  switch (field) {
    case 'fraud_status':
      return FRAUD_STATUS_LABELS[value as App.FraudStatus] ?? value;
    case 'status':
      return ORDER_STATUS_LABELS[value] ?? value;
    case 'utm_medium':
      return stores[value]?.displayName ?? value;
    default:
      return value;
  }
}

const sharedColumnProps = {
  sortable: false,
  disableColumnMenu: true,
  display: 'flex' as const,
};

function buildColumns(usersMap: Map<string, App.UserSummary>): Array<GridColDef<OrderEditLogEntry>> {
  return [
    {
      field: 'created_at',
      headerName: 'Date & Time',
      flex: 1,
      ...sharedColumnProps,
      valueFormatter: (value) => formatDateLongMonthWithMeridiemShort(value),
    },
    {
      field: 'changed_by_id',
      headerName: 'Changed By',
      flex: 0.8,
      ...sharedColumnProps,
      renderCell: ({ value }) => {
        const user = usersMap.get(value);
        return (
          <Link href={`/users/${value}`} underline="hover">
            {user?.full_name ?? value}
          </Link>
        );
      },
    },
    {
      field: 'field_name',
      headerName: 'Field',
      flex: 0.7,
      ...sharedColumnProps,
      valueFormatter: (value: OrderEditLogField) => ORDER_EDIT_LOG_FIELD_LABELS[value] ?? value,
    },
    {
      field: 'previous_value',
      headerName: 'Previous Value',
      flex: 1,
      ...sharedColumnProps,
      renderCell: ({ row }) => formatFieldValue(row.field_name, row.previous_value),
    },
    {
      field: 'new_value',
      headerName: 'New Value',
      flex: 1,
      ...sharedColumnProps,
      renderCell: ({ row }) => formatFieldValue(row.field_name, row.new_value),
    },
  ];
}

interface Props {
  orderId: string;
  brand: string;
}

export default function OrderEditLogs({ orderId, brand }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: PAGE_SIZE });

  const { data, isLoading } = useGetOrderEditLogs(
    orderId,
    paginationModel.page + 1,
    paginationModel.pageSize,
    expanded,
  );

  const userIds = useMemo(() => (data?.result ?? []).map((entry) => entry.changed_by_id), [data?.result]);

  const { data: usersMap = new Map() } = useGetUsersSummaryByIds({
    userIds,
    brand,
    enabled: userIds.length > 0,
  });

  const columns = useMemo(() => buildColumns(usersMap), [usersMap]);

  return (
    <Box mt={3}>
      <Accordion
        expanded={expanded}
        onChange={(_, isExpanded) => setExpanded(isExpanded)}
        variant="outlined"
        slotProps={{ transition: { unmountOnExit: true } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center">
            <EditNoteOutlinedIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Order Edit Log</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {isLoading && !data ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <DataGrid
              rows={data?.result ?? []}
              columns={columns}
              rowCount={data?.total ?? 0}
              loading={isLoading}
              getRowId={(row) => row.id_order_edit_logs}
              paginationMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[PAGE_SIZE]}
              disableColumnFilter
              disableColumnMenu
              disableRowSelectionOnClick
              autoHeight
            />
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
